import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { CircleAlert, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { BsSteam } from 'react-icons/bs';
import { toast } from 'sonner';
import { z } from 'zod';

import logo from '@/assets/Logo.svg';
import { PasswordInput } from '@/components/forms/password-input-simple';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { getSessionQueryOptions } from '@/hooks/useSession';
import { useSignInDialog } from '@/stores/SignInDialog';
import { cn } from '@/utils/cn';

import { useLogin } from '../api/useLogin';
import { useSteamLogin } from '../api/useSteamLogin';

const formSchema = z.object({
  email: z
    .string({
      required_error: 'Email is required',
    })
    .email('Invalid email format'),
  password: z
    .string({
      required_error: 'Password is required',
    })
    .min(1, 'Password is required'),
});

type SignInFormValues = z.infer<typeof formSchema>;

export function SignInDialog() {
  const { isOpen, close } = useSignInDialog();
  const { login: steamLogin } = useSteamLogin();
  const loginMutation = useLogin();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values: SignInFormValues) => {
    setIsLoading(true);
    loginMutation.mutate(
      { data: values },
      {
        onSuccess: (data) => {
          queryClient.setQueryData(getSessionQueryOptions().queryKey, data);
          toast.success('Signed in successfully!');
          close();
        },
        onError: (error: any) => {
          setIsLoading(false);
          if (error.data?.fields) {
            Object.entries(error.data.fields).forEach(([field, message]) => {
              form.setError(field as keyof SignInFormValues, {
                type: 'server',
                message: message as string,
              });
            });
          }
        },
      },
    );
  };

  const handleError = (errors: any) => {
    for (const key in errors) {
      toast.error(errors[key]?.message || `Error in ${key}`, {
        icon: <CircleAlert size={16} className="text-destructive" />,
      });
    }
  };

  const { isValid, isDirty } = form.formState;

  useEffect(() => {
    form.reset();
  }, [form, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <div className="flex flex-col items-center gap-2">
          <div
            className="flex size-11 shrink-0 items-center justify-center rounded-full border"
            aria-hidden="true"
          >
            <img className="h-8 w-auto p-1" src={logo} alt="SCL Logo" />
          </div>
          <DialogHeader>
            <DialogTitle className="sm:text-center">Welcome back!</DialogTitle>
            <DialogDescription className="sm:text-center">
              Please enter your details.
            </DialogDescription>
          </DialogHeader>
        </div>

        <Form {...form}>
          <form
            className={cn('grid items-start gap-4')}
            onSubmit={form.handleSubmit(onSubmit, handleError)}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="yourname@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <PasswordInput control={form.control} name="password" />
            <Button type="submit" disabled={!isDirty || !isValid || isLoading}>
              {isLoading ? <Loader2 className="animate-spin" /> : 'Sign in'}
            </Button>
          </form>
        </Form>

        <div className="flex items-center gap-3 before:h-px before:flex-1 before:bg-border after:h-px after:flex-1 after:bg-border">
          <span className="text-xs text-muted-foreground">Or</span>
        </div>

        <div className="flex flex-col gap-4">
          <Button
            variant="outline"
            onClick={steamLogin}
            className="w-full"
            disabled={isLoading}
          >
            <BsSteam className="" />
            Login with Steam
          </Button>

          <div className="text-center text-xs  text-muted-foreground">
            Don&apos;t have an account?{' '}
            <a href="/register" className="text-primary hover:underline">
              Register here
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
